// Shared Tailwind class sets (kept as whole strings so Tailwind's scanner sees every class).

/** Form controls' shared look; `CONTROL` and `CONTROL_SM` (below) add padding and text size (height is per control). */
// In the iOS app (`ios:`): iOS's filled fields, no frame, nor a ring when focused (the caret and keyboard say it). In
// the Android app (`android:`): Material's filled fields, a line under them, thicker and in the accent when focused.
const CONTROL_LOOK =
  "block w-full rounded-md border border-zinc-300 bg-white text-zinc-900 shadow-xs placeholder:text-zinc-400 focus:border-blue-600 focus:outline-2 focus:-outline-offset-1 focus:outline-blue-600 disabled:bg-zinc-50 disabled:text-zinc-500 aria-invalid:border-red-500 ios:rounded-[10px] ios:border-transparent ios:bg-zinc-950/6 ios:shadow-none ios:focus:border-transparent ios:focus:outline-none ios:aria-invalid:border-red-500 android:rounded-t-[4px] android:rounded-b-none android:border-0 android:border-b android:border-zinc-500 android:bg-zinc-950/6 android:shadow-none android:focus:border-primary android:focus:shadow-[inset_0_-1px_0_var(--color-primary)] android:focus:outline-none android:aria-invalid:border-red-600";

type Variant = "primary" | "secondary" | "danger" | "ghost";
type Size = "xs" | "sm" | "md";

/** The keyboard focus ring, the same on every button and control that's clicked. */
export const FOCUS = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
/**
 * An action with an icon: by it alone on a narrow screen (lib/barLabels.ts says), its words still its name (shrunk to
 * nothing, as the Pager's chevrons).
 */
export const ICON_ONLY_NARROW = "bar-icons:max-sm:gap-0 bar-icons:max-sm:text-[0px]";

/** A quiet hover (and open, or pressed) tint that works on any background: white, the sidebar's gray, a row's. */
const SHADE = "hover:bg-zinc-950/5";

// In the iOS app (`ios:`): iOS's buttons, rounder, dimmed while pressed, as tall as a finger needs; the secondary
// ones filled gray rather than framed (the accent's the main ones'). In the Android app (`android:`): Material 3
// Expressive's, round, squarer while pressed (on its spring); the secondary ones tonal.
const BUTTON = `inline-flex items-center justify-center gap-1.5 rounded-md border font-medium whitespace-nowrap transition-colors ${FOCUS} disabled:cursor-not-allowed disabled:opacity-50 ios:rounded-xl ios:transition ios:active:opacity-60 android:rounded-full android:transition-[border-radius,background-color] android:duration-(--md-spatial-duration) android:ease-(--md-spatial) android:active:rounded-lg`;
const VARIANTS: Record<Variant, string> = {
  primary: "border-transparent bg-primary text-on-fill hover:bg-primary-hover",
  secondary:
    "border-zinc-300 bg-white text-zinc-900 shadow-xs hover:bg-zinc-50 ios:border-transparent ios:bg-zinc-950/6 ios:shadow-none android:border-transparent android:bg-blue-100 android:text-blue-900 android:shadow-none android:hover:bg-blue-100 android:active:bg-blue-200",
  danger: "border-transparent bg-danger text-on-fill hover:bg-danger-hover",
  ghost: "border-transparent text-zinc-700 hover:bg-zinc-100",
};
const SIZES: Record<Size, string> = {
  xs: "h-7 px-2 text-xs ios:h-8 android:h-8 android:px-3", // as tall as compact controls
  sm: "h-8 px-2.5 text-sm ios:h-9 android:h-9 android:px-4",
  md: "h-9 px-3.5 text-sm ios:h-11 ios:px-4 ios:text-base android:h-10 android:px-6",
};

export type { Size, Variant };

export const buttonClass = (variant: Variant = "secondary", size: Size = "md"): string =>
  `${BUTTON} ${VARIANTS[variant]} ${SIZES[size]}`;

/**
 * Icon-only buttons, square: `2xs` for inline toggles (a tree's chevron), then as tall as `Button`'s sizes. Ghost (no
 * frame) by default, or framed like a secondary `Button` beside other buttons.
 */
const ICON_SIZES = {
  "2xs": "size-5 rounded",
  xs: "size-7 rounded-md ios:size-8 ios:rounded-xl android:size-8 android:rounded-full",
  sm: "size-8 rounded-md ios:size-9 ios:rounded-xl android:size-10 android:rounded-full",
  md: "size-9 rounded-md ios:size-11 ios:rounded-xl android:size-10 android:rounded-full",
} as const;
type IconSize = keyof typeof ICON_SIZES;
const ICON_VARIANTS = {
  ghost: `border-transparent text-zinc-500 ${SHADE} hover:text-zinc-900 aria-expanded:bg-zinc-950/5 aria-expanded:text-zinc-900 aria-pressed:text-blue-600`,
  secondary: VARIANTS.secondary,
} as const;
type IconVariant = keyof typeof ICON_VARIANTS;

export type { IconSize, IconVariant };

export const iconButtonClass = (size: IconSize = "sm", variant: IconVariant = "ghost"): string =>
  `inline-flex shrink-0 items-center justify-center border transition-colors ${FOCUS} disabled:cursor-not-allowed disabled:opacity-50 ${ICON_VARIANTS[variant]} ${ICON_SIZES[size]}`;

/**
 * An action inside a line of text: muted (gray, the usual), as a link (blue) when it's the way forward, or red when it
 * deletes or removes.
 */
type InlineTone = "muted" | "link" | "danger";

export type { InlineTone };

const INLINE_TONES: Record<InlineTone, string> = {
  muted: "text-zinc-500 hover:text-zinc-900",
  link: "font-medium text-blue-700",
  danger: "text-red-700 hover:text-red-800",
};

export const inlineButtonClass = (tone: InlineTone = "muted", size: "xs" | "sm" = "sm"): string =>
  `inline-flex items-center gap-1 rounded-sm hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline ${FOCUS} ${size === "xs" ? "text-xs" : "text-sm"} ${INLINE_TONES[tone]}`;

/** One choice in a grid of them (emoji, skin tones, icons): no frame, tinted on hover, blue when chosen. */
export const TILE = `inline-flex items-center justify-center rounded-md leading-none text-zinc-700 ${SHADE} aria-pressed:bg-blue-100 aria-pressed:text-blue-700 ${FOCUS}`;

export type Tone = "gray" | "red" | "amber" | "blue" | "green";

/** Each tone: a soft tint, its text, the ring around it, and (when it does something) its hover. Chips and chosen
 * segments wear them. */
export const TONES: Record<Tone, { look: string; hover: string }> = {
  gray: { look: "bg-zinc-100 text-zinc-700 ring-zinc-200", hover: "hover:bg-zinc-200" },
  red: { look: "bg-red-50 text-red-700 ring-red-200", hover: "hover:bg-red-100" },
  amber: { look: "bg-amber-50 text-amber-800 ring-amber-200", hover: "hover:bg-amber-100" },
  blue: { look: "bg-blue-50 text-blue-700 ring-blue-200", hover: "hover:bg-blue-100" },
  green: { look: "bg-green-50 text-green-700 ring-green-200", hover: "hover:bg-green-100" },
};

export const CONTROL = `${CONTROL_LOOK} px-3 py-1.5 text-sm`;
/** A control's look without its padding and size (a search grown from its icon, its icon inside). */
export const CONTROL_FRAME = CONTROL_LOOK;
/** Compact controls, for secondary rows (a ref field's link fields). */
export const CONTROL_SM = `${CONTROL_LOOK} px-2 py-0.5 text-xs`;

/**
 * A row of fields and their buttons, lined up by the controls: fields align at the top (so a hint or error under one
 * hangs below without lifting it), and buttons directly in the row drop by a label's height (a `text-sm` line plus the
 * Field's `gap-1.5`) to sit level with the inputs.
 */
export const FORM_ROW = "flex flex-wrap items-start gap-2 [&>button]:mt-6.5";

export const TH = "px-3 py-2 text-left font-medium whitespace-nowrap text-zinc-600";
export const TD = "px-3 py-2 align-top text-zinc-800";
export const LINK = "font-medium text-blue-700 hover:underline";
/** A link inside a warning (amber): in its colour, underlined so it still reads as one. */
export const LINK_WARNING = "font-medium text-amber-800 underline decoration-amber-400 hover:text-amber-950";

export const SEVERITY_TONE = { error: "red", warning: "amber", info: "blue" } as const;
/** Severities, most severe first. */
export const SEVERITIES = Object.keys(SEVERITY_TONE) as readonly (keyof typeof SEVERITY_TONE)[];

/** A docked look's width (half the window, from a tablet's width up; a phone's whole screen below). */
export const DOCKED_WIDTH = "md:w-[50vw]";

/** The room the app leaves a pinned look, by its side (as wide as the look). */
export const SPLIT = { left: "md:pl-[50vw]", right: "md:pr-[50vw]", none: "" } as const;

/** An agent's picture's circle (one of the app's, or its icon when it has none): the same edge for all. */
export const PICTURE_CIRCLE =
  "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-zinc-950/10 ring-inset";
/** What a logo (or an agent's icon) takes of its circle: the same optical size for all. */
export const PICTURE_INNER = "size-[55%]";

/** Each level's color (the icon and its count). */
export const LEVEL_COLOR = {
  error: "text-red-600",
  warning: "text-yellow-500",
  info: "text-blue-600",
  triage: "text-zinc-500",
} as const;
